import { router } from '@inertiajs/react'
import { useState, useEffect } from 'react'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  CalendarIcon,
  CloudIcon,
  CheckCircleIcon,
  XCircleIcon,
  RefreshCwIcon,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ExternalEventDetailDialog } from '@/components/external-event-detail-dialog'
import authService from '@/lib/auth'

interface Appointment {
  id: number | string
  lead_id?: number
  lead_name?: string
  date: string
  time: string
  duration: number
  appointment_type: string
  technician: string
  status: string
  notes: string
  synced_to_google: boolean
  synced_to_team: boolean
  synced_to_personal: boolean
  created_at: string
  is_external?: boolean
}

interface Lead {
  id: number
  name: string
}

interface Connection {
  id: number
  calendar_type: string
  google_account_email: string
  user_name: string | null
  last_synced_at: string | null
  sync_enabled: boolean
  webhook_expires_at: string | null
}

interface GoogleCalendarIndexProps {
  appointments: Appointment[]
  view_type: string
  current_date: string
  leads: Lead[]
  connections: Connection[]
}

export default function GoogleCalendarIndex({
  appointments,
  view_type,
  current_date,
  leads,
  connections,
}: GoogleCalendarIndexProps) {
  const [viewType, setViewType] = useState(view_type)
  const [currentDate, setCurrentDate] = useState(new Date(current_date))
  const [filterType, setFilterType] = useState<'all' | 'team' | 'personal'>('all')
  const [externalEventDialogOpen, setExternalEventDialogOpen] = useState(false)
  const [selectedExternalEvent, setSelectedExternalEvent] = useState<any>(null)
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null)

  // Helper function to format date in local timezone as YYYY-MM-DD
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const handleViewChange = (view: string) => {
    setViewType(view)
    router.get(
      '/google_calendar',
      {
        view,
        date: formatDateLocal(currentDate),
      },
      {
        preserveState: true,
        preserveScroll: true,
      }
    )
  }

  const handleDateChange = (direction: 'prev' | 'next') => {
    let newDate = new Date(currentDate)

    if (viewType === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1))
    } else if (viewType === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7))
    } else {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1))
    }

    setCurrentDate(newDate)
    router.get(
      '/google_calendar',
      {
        view: viewType,
        date: formatDateLocal(newDate),
      },
      {
        preserveState: true,
        preserveScroll: true,
      }
    )
  }

  const handleToday = () => {
    const today = new Date()
    setCurrentDate(today)
    router.get(
      '/google_calendar',
      {
        view: viewType,
        date: formatDateLocal(today),
      },
      {
        preserveState: true,
        preserveScroll: true,
      }
    )
  }

  const handleConnectPersonalCalendar = () => {
    router.visit('/google_calendar/oauth/authorize')
  }

  const handleDisconnectPersonalCalendar = () => {
    if (confirm('Are you sure you want to disconnect your Google Calendar?')) {
      router.delete('/google_calendar/oauth/disconnect')
    }
  }

  const handleExternalEventClick = async (eventId: string | number, connection: Connection) => {
    try {
      const response = await fetch(
        `/google_calendar/external_events/${eventId}.json?connection_id=${connection.id}`,
        {
          headers: {
            ...authService.authHeaders(),
            'Accept': 'application/json',
          },
        }
      )

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      setSelectedExternalEvent(data.event)
      setSelectedConnection(data.connection)
      setExternalEventDialogOpen(true)
    } catch (error) {
      console.error('Failed to fetch event details:', error)
    }
  }

  const handleAppointmentClick = async (appointmentId: number) => {
    try {
      const response = await fetch(`/appointments/${appointmentId}.json`, {
        headers: {
          ...authService.authHeaders(),
          'Accept': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      setSelectedAppointment(data.appointment)
      setAppointmentDialogOpen(true)
    } catch (error) {
      console.error('Failed to fetch appointment details:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Scheduled':
        return 'bg-blue-600 hover:bg-blue-700'
      case 'In Progress':
        return 'bg-yellow-600 hover:bg-yellow-700'
      case 'Completed':
        return 'bg-green-600 hover:bg-green-700'
      case 'Cancelled':
        return 'bg-gray-600 hover:bg-gray-700'
      default:
        return 'bg-gray-600 hover:bg-gray-700'
    }
  }

  const formatDateHeader = () => {
    if (viewType === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    } else if (viewType === 'week') {
      const weekStart = new Date(currentDate)
      weekStart.setDate(currentDate.getDate() - currentDate.getDay())
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekStart.getDate() + 6)
      return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    } else {
      return currentDate.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    }
  }

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()

    const days = []

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null)
    }

    for (let day = 1; day <= daysInMonth; day++) {
      // Create date at noon to avoid midnight timezone boundary issues
      days.push(new Date(year, month, day, 12, 0, 0))
    }

    return days
  }

  const getAppointmentsForDate = (date: Date | null) => {
    if (!date) return []
    return filteredAppointments.filter((apt) => apt.date === formatDateLocal(date))
  }

  const filteredAppointments = appointments.filter((apt) => {
    if (filterType === 'all') return true
    if (filterType === 'team') return apt.synced_to_team
    if (filterType === 'personal') return apt.synced_to_personal
    return true
  })

  const personalConnection = connections.find((c) => c.calendar_type === 'personal')
  const teamConnection = connections.find((c) => c.calendar_type === 'team')

  const renderMonthView = () => {
    const days = getDaysInMonth(currentDate)
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    return (
      <div className="grid grid-cols-7 gap-px bg-muted">
        {weekDays.map((day) => (
          <div key={day} className="bg-background p-2 text-center font-semibold text-sm">
            {day}
          </div>
        ))}

        {days.map((day, index) => {
          const dayAppointments = day ? getAppointmentsForDate(day) : []
          const isToday = day && day.toDateString() === new Date().toDateString()

          return (
            <div
              key={index}
              className={`bg-background min-h-24 p-2 ${!day ? 'bg-muted/50' : ''} ${isToday ? 'bg-orange-50 dark:bg-orange-950/20' : ''}`}
            >
              {day && (
                <>
                  <div
                    className={`text-sm font-medium mb-1 ${isToday ? 'text-orange-600 font-bold' : ''}`}
                  >
                    {day.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayAppointments.map((apt) => (
                      <div
                        key={apt.id}
                        className={`text-xs p-1 rounded cursor-pointer border hover:border-orange-600 ${
                          apt.is_external
                            ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-200 dark:border-purple-800'
                            : 'bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'
                        }`}
                        onClick={() => {
                          if (apt.is_external) {
                            // Find the connection for this external event
                            const connection = connections.find(
                              (c) =>
                                (apt.synced_to_team && c.calendar_type === 'team') ||
                                (apt.synced_to_personal && c.calendar_type === 'personal')
                            )
                            if (connection) {
                              handleExternalEventClick(apt.id, connection)
                            }
                          } else {
                            // Show appointment details in popup
                            handleAppointmentClick(apt.id as number)
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-medium truncate">{apt.time}</div>
                          {apt.synced_to_google && (
                            <CloudIcon className="h-3 w-3 text-green-600" />
                          )}
                        </div>
                        <div className="truncate text-muted-foreground">
                          {apt.is_external ? apt.appointment_type : apt.lead_name}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
          <div className="px-4 lg:px-6">
            <div className="flex flex-col gap-4">
              {/* Header */}
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">Google Calendar</h1>
                  <p className="text-sm text-muted-foreground">
                    Synced appointments from Google Calendar
                  </p>
                </div>
                <Button onClick={() => setDialogOpen(true)} data-testid="create-appointment-button">
                  <PlusIcon className="mr-2 h-4 w-4" />
                  New Appointment
                </Button>
              </div>

              {/* Connection Status Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Calendar Connections</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Team Connection */}
                    {teamConnection ? (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircleIcon className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium">Team</span>
                          </div>
                          <span className={`text-xs ${teamConnection.sync_enabled ? 'text-green-600' : 'text-orange-600'}`}>
                            Sync: {teamConnection.sync_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {teamConnection.google_account_email}
                        </div>
                        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                          {teamConnection.last_synced_at && (
                            <div>
                              Last synced: {new Date(teamConnection.last_synced_at).toLocaleString()}
                            </div>
                          )}
                          {teamConnection.webhook_expires_at && (
                            <div>
                              Webhook expires: {new Date(teamConnection.webhook_expires_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <XCircleIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Team calendar not configured</span>
                      </div>
                    )}

                    {/* Personal Connection */}
                    {personalConnection && (
                      <div className="space-y-2 pt-4 border-t">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircleIcon className="h-4 w-4 text-green-600" />
                            <span className="text-sm font-medium">Personal</span>
                          </div>
                          <span className={`text-xs ${personalConnection.sync_enabled ? 'text-green-600' : 'text-orange-600'}`}>
                            Sync: {personalConnection.sync_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {personalConnection.google_account_email}
                        </div>
                        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
                          {personalConnection.last_synced_at && (
                            <div>
                              Last synced: {new Date(personalConnection.last_synced_at).toLocaleString()}
                            </div>
                          )}
                          {personalConnection.webhook_expires_at && (
                            <div>
                              Webhook expires: {new Date(personalConnection.webhook_expires_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDisconnectPersonalCalendar}
                        >
                          Disconnect
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Calendar Controls */}
              <Card>
                <CardHeader>
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleDateChange('prev')}>
                        <ChevronLeftIcon className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={handleToday}>
                        Today
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleDateChange('next')}>
                        <ChevronRightIcon className="h-4 w-4" />
                      </Button>
                      <h2 className="ml-4 text-lg font-semibold">{formatDateHeader()}</h2>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Filter Tabs */}
                      <Tabs value={filterType} onValueChange={(v) => setFilterType(v as any)}>
                        <TabsList>
                          <TabsTrigger value="all">All</TabsTrigger>
                          <TabsTrigger value="team">Team</TabsTrigger>
                          <TabsTrigger value="personal">Personal</TabsTrigger>
                        </TabsList>
                      </Tabs>

                      {/* View Tabs */}
                      <Tabs value={viewType} onValueChange={handleViewChange}>
                        <TabsList>
                          <TabsTrigger value="month">Month</TabsTrigger>
                          <TabsTrigger value="week">Week</TabsTrigger>
                          <TabsTrigger value="day">Day</TabsTrigger>
                        </TabsList>
                      </Tabs>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>{renderMonthView()}</CardContent>
              </Card>

              {/* Sync Statistics */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Sync Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <div className="text-2xl font-bold">{appointments.length}</div>
                      <div className="text-xs text-muted-foreground">Total Appointments</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        {appointments.filter((a) => a.synced_to_google).length}
                      </div>
                      <div className="text-xs text-muted-foreground">Synced to Google</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        {appointments.filter((a) => !a.synced_to_google).length}
                      </div>
                      <div className="text-xs text-muted-foreground">Not Synced</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <ExternalEventDetailDialog
        open={externalEventDialogOpen}
        onOpenChange={setExternalEventDialogOpen}
        event={selectedExternalEvent}
        connection={selectedConnection}
      />
    </div>
  )
}
